import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, DollarSign, TrendingDown, Calendar, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const RATE = 0.67;
const MIN = 50;
const MAX = 50000;
const DEFAULT = 1000;
const DEV_MIN = 2;
const DEV_MAX = 500;
const DEV_DEFAULT = 10;

function useAnimatedValue(target, duration = 600) {
  const [value, setValue] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = value;
    const diff = target - start;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setValue(start + diff * ease);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);
  return value;
}

function fmt(n) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}

export default function SavingsCalculator() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("individual");
  const [spend, setSpend] = useState(DEFAULT);
  const [inputText, setInputText] = useState(String(DEFAULT));
  const [devs, setDevs] = useState(DEV_DEFAULT);
  const [devInput, setDevInput] = useState(String(DEV_DEFAULT));

  const isTeam = mode === "team";
  const perDevSaved = spend * RATE;
  const totalMonthlySaved = isTeam ? perDevSaved * devs : perDevSaved;
  const totalAnnualSaved = totalMonthlySaved * 12;

  const animMonthly = useAnimatedValue(totalMonthlySaved);
  const animAnnual = useAnimatedValue(totalAnnualSaved);
  const animPct = useAnimatedValue(RATE * 100, 400);
  const animPerDev = useAnimatedValue(perDevSaved);

  const onSlider = (e) => {
    const v = Number(e.target.value);
    setSpend(v);
    setInputText(v.toLocaleString());
  };

  const onInput = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    setInputText(raw);
    const v = Number(raw);
    if (v >= MIN && v <= MAX) setSpend(v);
  };

  const onBlur = () => {
    const v = Math.max(MIN, Math.min(MAX, Number(inputText.replace(/[^0-9]/g, "")) || MIN));
    setSpend(v);
    setInputText(v.toLocaleString());
  };

  const onDevInput = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    setDevInput(raw);
    const v = Number(raw);
    if (v >= DEV_MIN && v <= DEV_MAX) setDevs(v);
  };

  const onDevBlur = () => {
    const v = Math.max(DEV_MIN, Math.min(DEV_MAX, Number(devInput.replace(/[^0-9]/g, "")) || DEV_MIN));
    setDevs(v);
    setDevInput(String(v));
  };

  const onDevSlider = (e) => {
    const v = Number(e.target.value);
    setDevs(v);
    setDevInput(String(v));
  };

  const spendPct = ((spend - MIN) / (MAX - MIN)) * 100;
  const devPct = ((devs - DEV_MIN) / (DEV_MAX - DEV_MIN)) * 100;

  return (
    <section className="py-24 relative overflow-hidden" data-testid="savings-calculator-section" data-automation-id="savings-calculator-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[var(--qn-primary)]/[0.03] blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block text-xs font-semibold tracking-wider uppercase px-3 py-1 rounded-full bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border border-[var(--qn-primary)]/20 mb-4">
            Savings Calculator
          </span>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight mb-3">
            See How Much You Could Save
          </h2>
          <p className="text-[var(--qn-foreground-muted)] max-w-lg mx-auto text-base">
            Enter your current monthly AI spend and see your projected savings with QNNEAL's 67% average cost reduction.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-8 md:p-10"
          data-testid="savings-calculator-card" data-automation-id="savings-calculator-card"
        >
          {/* Mode Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] p-1" data-testid="calc-mode-toggle" data-automation-id="calc-mode-toggle">
              {["individual", "team"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  data-testid={`calc-mode-${m}`}
                  className={`px-5 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                    mode === m
                      ? "bg-[var(--qn-primary)] text-white shadow-sm"
                      : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
                  }`}
                >
                  {m === "team" && <Users className="w-3.5 h-3.5" />}
                  {m === "individual" ? "Individual" : "Team"}
                </button>
              ))}
            </div>
          </div>

          {/* Inputs */}
          <div className={`grid gap-8 mb-10 ${isTeam ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
            {/* Spend input */}
            <div>
              <label className="block text-sm font-medium text-[var(--qn-foreground-muted)] mb-3">
                {isTeam ? "AI Spend per Developer" : "Your Monthly AI Spend"}
              </label>
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-[180px]">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--qn-foreground-subtle)]">$</span>
                  <input
                    type="text"
                    value={inputText}
                    onChange={onInput}
                    onBlur={onBlur}
                    className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] text-lg font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]/40 focus:border-[var(--qn-primary)]"
                    data-testid="savings-spend-input" data-automation-id="savings-spend-input"
                  />
                </div>
                <span className="text-sm text-[var(--qn-foreground-subtle)] hidden sm:inline">/mo{isTeam ? " each" : ""}</span>
              </div>
              <input
                type="range"
                min={MIN}
                max={MAX}
                step={50}
                value={spend}
                onChange={onSlider}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to right, var(--qn-primary) 0%, var(--qn-primary) ${spendPct}%, var(--qn-border) ${spendPct}%, var(--qn-border) 100%)` }}
                data-testid="savings-spend-slider" data-automation-id="savings-spend-slider"
              />
              <div className="flex justify-between mt-2 text-xs text-[var(--qn-foreground-subtle)]">
                <span>$50</span>
                <span>$50,000</span>
              </div>
            </div>

            {/* Team size input */}
            <AnimatePresence>
              {isTeam && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.25 }}
                >
                  <label className="block text-sm font-medium text-[var(--qn-foreground-muted)] mb-3">
                    Number of Developers
                  </label>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="relative flex-1 max-w-[120px]">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-subtle)]" />
                      <input
                        type="text"
                        value={devInput}
                        onChange={onDevInput}
                        onBlur={onDevBlur}
                        className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] text-lg font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]/40 focus:border-[var(--qn-primary)]"
                        data-testid="savings-devs-input" data-automation-id="savings-devs-input"
                      />
                    </div>
                    <span className="text-sm text-[var(--qn-foreground-subtle)] hidden sm:inline">developers</span>
                  </div>
                  <input
                    type="range"
                    min={DEV_MIN}
                    max={DEV_MAX}
                    step={1}
                    value={devs}
                    onChange={onDevSlider}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{ background: `linear-gradient(to right, var(--qn-primary) 0%, var(--qn-primary) ${devPct}%, var(--qn-border) ${devPct}%, var(--qn-border) 100%)` }}
                    data-testid="savings-devs-slider" data-automation-id="savings-devs-slider"
                  />
                  <div className="flex justify-between mt-2 text-xs text-[var(--qn-foreground-subtle)]">
                    <span>2</span>
                    <span>500</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Team breakdown line */}
          <AnimatePresence>
            {isTeam && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="text-center mb-6 px-4 py-3 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)]" data-testid="team-breakdown" data-automation-id="team-breakdown">
                  <span className="text-sm text-[var(--qn-foreground-muted)]">
                    <span className="font-mono font-semibold text-[var(--qn-foreground)]">{devs}</span> developers
                    {" "}&times;{" "}
                    <span className="font-mono font-semibold text-[var(--qn-primary)]">{fmt(animPerDev)}/mo</span> each
                    {" "}={" "}
                    <span className="font-mono font-bold text-[var(--qn-primary)]">{fmt(animMonthly)}/mo</span> team savings
                    {" "}
                    <span className="text-[var(--qn-foreground-subtle)]">({fmt(animAnnual)}/year)</span>
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Results */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="savings-results" data-automation-id="savings-results">
            <motion.div
              className="relative rounded-xl p-6 border border-[var(--qn-primary)]/20 bg-[var(--qn-primary)]/[0.05] text-center overflow-hidden"
              whileHover={{ scale: 1.02 }}
            >
              <div className="absolute inset-0 pointer-events-none rounded-xl" style={{ boxShadow: "inset 0 0 40px rgba(0,240,255,0.06)" }} />
              <DollarSign className="w-5 h-5 text-[var(--qn-primary)] mx-auto mb-2" />
              <div className="text-sm text-[var(--qn-foreground-muted)] mb-1">{isTeam ? "Team Monthly Savings" : "Monthly Savings"}</div>
              <div
                className="font-mono font-bold text-3xl text-[var(--qn-primary)]"
                style={{ textShadow: "0 0 20px rgba(0,240,255,0.3)" }}
                data-testid="savings-monthly" data-automation-id="savings-monthly"
              >
                {fmt(animMonthly)}
              </div>
            </motion.div>

            <motion.div
              className="relative rounded-xl p-6 border border-[var(--qn-secondary)]/20 bg-[var(--qn-secondary)]/[0.05] text-center overflow-hidden"
              whileHover={{ scale: 1.02 }}
            >
              <div className="absolute inset-0 pointer-events-none rounded-xl" style={{ boxShadow: "inset 0 0 40px rgba(0,212,170,0.06)" }} />
              <Calendar className="w-5 h-5 text-[var(--qn-secondary)] mx-auto mb-2" />
              <div className="text-sm text-[var(--qn-foreground-muted)] mb-1">{isTeam ? "Team Annual Savings" : "Annual Savings"}</div>
              <div
                className="font-mono font-bold text-3xl text-[var(--qn-secondary)]"
                style={{ textShadow: "0 0 20px rgba(0,212,170,0.3)" }}
                data-testid="savings-annual" data-automation-id="savings-annual"
              >
                {fmt(animAnnual)}
              </div>
            </motion.div>

            <motion.div
              className="relative rounded-xl p-6 border border-emerald-500/20 bg-emerald-500/[0.05] text-center overflow-hidden"
              whileHover={{ scale: 1.02 }}
            >
              <div className="absolute inset-0 pointer-events-none rounded-xl" style={{ boxShadow: "inset 0 0 40px rgba(16,185,129,0.06)" }} />
              <TrendingDown className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
              <div className="text-sm text-[var(--qn-foreground-muted)] mb-1">Cost Reduction</div>
              <div
                className="font-mono font-bold text-3xl text-emerald-400"
                style={{ textShadow: "0 0 20px rgba(16,185,129,0.3)" }}
                data-testid="savings-percent" data-automation-id="savings-percent"
              >
                {Math.round(animPct)}%
              </div>
            </motion.div>
          </div>

          {/* CTA */}
          <div className="mt-8 text-center">
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block rounded-full">
              <Button
                className="btn-primary text-base px-8 py-5 rounded-full"
                onClick={() => navigate("/login")}
                data-testid="savings-cta-btn" data-automation-id="savings-cta-btn"
              >
                {isTeam ? `Start Saving ${fmt(animMonthly)}/mo for Your Team` : `Start Saving ${fmt(animMonthly)}/mo`}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
            <p className="text-xs text-[var(--qn-foreground-subtle)] mt-3">
              No credit card required. Free tier includes 20 optimizations/month.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
